import React from 'react';
import classNames from 'classnames';
import appManager from '@app/appManager';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import TasksModule from '@modules/TasksModule';
import SoftPhoneModule from '@modules/SoftPhoneModule';
import InboxModule from '@modules/InboxModule';
import ToastModule from '@modules/ToastModule';
import { Headline, BodyText } from '@servicetitan/design-system';
import { Flex } from '@servicetitan/anvil2';
import { SpeedToLeadCallButton } from '../SpeedToLeadCallButton';
import { LogOutcomeSection } from '../LogOutcomeSection/LogOutcomeSection';
import { completeTask } from '@modules/SpeedToLeadTasksModule/mutations/completeTask';
import { SpeedToLeadTaskAttributes } from '@modules/SpeedToLeadTasksModule/types';
import { TaskOutcome } from '@api/generated/graphql';
import AuthModule from '@modules/AuthModule';
import { bookJobLinkToMonolith } from '@lib/utils/tenantLinks';
import { cancelAcceptanceExpiry } from '@modules/SpeedToLeadTasksModule/acceptanceExpiry';
import { FeatureFlagKeys, useFeatureFlag } from '@modules/FeatureFlagModule';
import styles from './SpeedToLeadTaskView.module.css';
import useTenantName from '@lib/hooks/useTenantName';

const logger = appManager.logger('SpeedToLead');

export interface SpeedToLeadTaskViewProps {
  taskId: string;
  show?: boolean;
}

export const SpeedToLeadTaskView: React.FC<SpeedToLeadTaskViewProps> = ({
  taskId,
  show = false,
}) => {
  const { taskById } = useTasks();
  const task = taskById[taskId];
  const tenantName = useTenantName(task?.tenantId);
  const isAiOutcomeEnabled = useFeatureFlag(
    FeatureFlagKeys.featureSpeedToLeadOutboundingAiTaskOutcome,
    { defaultValue: false },
  );

  if (!task) {
    return null;
  }

  const attributes = task.attributes as SpeedToLeadTaskAttributes;
  const { contact, taskDetails, action } = attributes;

  // Call state is managed via task attributes by STL call event handlers
  const hasMadeCall = !!attributes.hasMadeCall;
  const hasCallConnected = !!attributes.hasCallConnected;
  const hasCallEnded = !!attributes.hasCallEnded;
  // Show the outcome section if a dial has been attempted, even during the
  // redial window (hasMadeCall is reset to false on telephony failure so the
  // button re-enables, but the section should remain visible).
  const showOutcomeSection =
    !isAiOutcomeEnabled && (hasMadeCall || !!attributes.hasUsedRedial);

  const customerPhone = contact.phoneNumbers[0] ?? '';

  const handleCall = async () => {
    // Cancel the acceptance expiry — once the CSR dials, the task transitions to IN_CALL
    // and the 60-second window no longer applies.
    cancelAcceptanceExpiry();
    // Set hasMadeCall before placing the call so that InboxModule's handleCallPlaced
    // (which fires first due to module registration order) sees it and skips task creation.
    TasksModule.setTaskAttribute(taskId, 'hasMadeCall', true);
    logger.info('stl:dial:initiated', {
      taskId,
      tenantId: attributes.tenantId,
    });
    try {
      await SoftPhoneModule.placeOutboundCall(customerPhone, {
        name: tenantName || '',
        number: attributes.outboundNumber,
        tenantId: attributes.tenantId,
      });
    } catch (err) {
      logger.error('stl:dial:failed', {
        taskId,
        tenantId: attributes.tenantId,
        err,
      });
      ToastModule.showToast({
        title: 'Failed to place call. Please try again.',
        status: 'critical',
      });
    }
  };

  const handleBookJob = () => {
    window.open(bookJobLinkToMonolith(attributes.tenantId), '_blank');
    AuthModule.resetAuthAge();
  };

  const handleSubmitOutcome = async (outcome: TaskOutcome, notes?: string) => {
    logger.info('stl:outcome:submitting', {
      taskId,
      tenantId: attributes.tenantId,
      outcome,
    });
    try {
      await completeTask({
        taskId,
        tenantId: attributes.tenantId,
        input: {
          outcome,
          notes,
        },
      });
      logger.info('stl:outcome:submitted', { taskId });
      TasksModule.deleteTask(taskId);
      InboxModule.setSelectedTaskId(undefined);
      ToastModule.showToast({
        title: 'Outbound task complete',
        status: 'success',
      });
    } catch (err) {
      logger.error('stl:outcome:submit-failed', {
        taskId,
        tenantId: attributes.tenantId,
        outcome,
        err,
      });
      ToastModule.showToast({
        title: 'Failed to log outcome. Please try again.',
        status: 'critical',
      });
    }
  };

  // Helper function to convert text with newlines into list items
  const renderBulletList = (text: string) => {
    const items = text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (items.length === 0) return null;

    return (
      <ul className={styles.bulletList}>
        {items.map((item, index) => (
          <li key={index}>
            <BodyText size='small'>{item}</BodyText>
          </li>
        ))}
      </ul>
    );
  };

  // Format address
  const address = contact.address;
  const addressString = [
    address.street,
    address.unit,
    address.city,
    address.state,
    address.zip,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div
      className={classNames(
        'w-100',
        'h-100',
        'of-y-auto',
        'p-x-3',
        'p-y-2',
        'bg-white',
        {
          'd-n': !show,
        },
      )}
    >
      {/* Customer Header */}
      <Flex direction='column' gap='2' className={styles.customerHeader}>
        <Flex justifyContent='space-between' alignItems='flex-start'>
          <div>
            <Headline size='large'>{contact.name}</Headline>
            <BodyText size='small' subdued>
              {addressString}
            </BodyText>
            <div className={styles.badge}>
              <BodyText size='xsmall'>Action: {action}</BodyText>
            </div>
          </div>
          <SpeedToLeadCallButton
            customerName={contact.name.split(' ')[0]}
            onCall={handleCall}
            onBookJob={handleBookJob}
            disabled={hasMadeCall}
            showBookJob={hasCallConnected}
            isCallConnected={hasCallConnected}
            isCallEnded={hasCallEnded}
          />
        </Flex>
      </Flex>

      {/* Log Outcome Section - shown after any dial attempt, including during the redial window */}
      {showOutcomeSection && (
        <LogOutcomeSection
          liveAnswerOutcomeOptions={attributes.liveAnswerOutcomeOptions ?? []}
          noAnswerOutcomeOptions={attributes.noAnswerOutcomeOptions ?? []}
          onSubmit={handleSubmitOutcome}
          isCallEnded={hasCallEnded}
        />
      )}

      {/* Task Details Section */}
      <div className={styles.taskDetails}>
        <Headline size='small' className={styles.sectionHeading}>
          Task Details
        </Headline>

        {taskDetails.leadSource && (
          <div className={styles.detailSection}>
            <BodyText size='small' bold>
              Source:
            </BodyText>
            <BodyText size='small'>{taskDetails.leadSource}</BodyText>
          </div>
        )}

        <div className={styles.detailSection}>
          <BodyText size='small' bold>
            Script Opener:
          </BodyText>
          <div className={styles.quotedText}>
            <BodyText size='small'>"{taskDetails.scriptOpener}"</BodyText>
          </div>
        </div>

        <div className={styles.detailSection}>
          <BodyText size='small' bold>
            Lead Summary:
          </BodyText>
          {renderBulletList(taskDetails.summary)}
        </div>

        <div className={styles.detailSection}>
          <BodyText size='small' bold>
            What Has Been Attempted:
          </BodyText>
          {renderBulletList(taskDetails.history)}
        </div>

        <div className={styles.detailSection}>
          <BodyText size='small' bold>
            Goal of the Call:
          </BodyText>
          {renderBulletList(taskDetails.goal)}
        </div>
      </div>
    </div>
  );
};
